import * as crypto from "./crypto";
import { DecryptionError } from "./crypto";
import * as store from "./local-store";
import { api, ApiError } from "./api";

let identityCache: { publicKey: string; privateKey: string } | null = null;

/**
 * Loads this device's identity keypair, generating and publishing a fresh one if
 * none exists locally. A freshly generated identity cannot decrypt any conversation
 * whose key was sealed to a previous identity - see backend/README.md's E2EE
 * limitations. Call this before any other function in this module.
 */
export async function ensureIdentity(options?: { confirmReplace?: boolean }): Promise<{ publicKey: string; privateKey: string }> {
  if (identityCache) return identityCache;

  const stored = await store.loadIdentity();
  if (stored) {
    identityCache = stored;
    return stored;
  }

  const kp = crypto.generateIdentityKeypair();
  await store.saveIdentity(kp.publicKey, kp.privateKey);
  // confirmReplace defaults to false - the server rejects overwriting an
  // already-established, different key unless this is explicitly true. See
  // auth-context.tsx's establishIdentity/confirmStartFresh for the only
  // caller that ever passes true, after an explicit user choice.
  await api.post("/api/users/me/public-key", { publicKey: kp.publicKey, confirmReplace: options?.confirmReplace ?? false });
  identityCache = kp;
  return kp;
}

/** Used at registration time, before the account exists server-side. */
export function generateRegistrationIdentity() {
  return crypto.generateIdentityKeypair();
}

export async function persistIdentity(kp: { publicKey: string; privateKey: string }) {
  await store.saveIdentity(kp.publicKey, kp.privateKey);
  identityCache = kp;
}

/** True if this device already has a local identity - checks without ever
 * generating one, unlike ensureIdentity(). */
export async function hasLocalIdentity(): Promise<boolean> {
  if (identityCache) return true;
  return !!(await store.loadIdentity());
}

export interface RecoveryBackupInfo {
  salt: string;
  encryptedPrivateKey: string;
  isAutomatic: boolean;
}

/** Checks the server for a backup on this account, without needing any secret
 * yet - just to know whether one exists at all before trying to open it. */
export async function checkServerRecoveryBackup(): Promise<RecoveryBackupInfo | null> {
  const result = await api.get<{ salt: string | null; encryptedPrivateKey: string | null; isAutomatic: boolean }>("/api/users/me/recovery-backup");
  if (!result.salt || !result.encryptedPrivateKey) return null;
  return { salt: result.salt, encryptedPrivateKey: result.encryptedPrivateKey, isAutomatic: result.isAutomatic };
}

/**
 * Attempts to restore this device's identity from a server-side backup using the
 * given secret (either the account's own login password - the automatic default -
 * or a separate custom passphrase, if one was set up). Throws
 * crypto.WrongRecoveryPassphraseError if the secret doesn't open this backup.
 * Also verifies the recovered key actually matches the account's CURRENT public
 * key before trusting it - guards against restoring a stale backup left over from
 * an identity that's since been replaced, which would otherwise silently install
 * a private key that no longer corresponds to what anyone else seals messages
 * against.
 */
export async function restoreIdentityFromRecovery(secret: string, backup: RecoveryBackupInfo): Promise<void> {
  const privateKey = crypto.decryptPrivateKeyBackup(backup.encryptedPrivateKey, secret, backup.salt);
  const derivedPublicKey = crypto.derivePublicKey(privateKey);

  const me = await api.get<{ public_key: string | null }>("/api/auth/me");
  if (me.public_key !== derivedPublicKey) {
    throw new StaleRecoveryBackupError();
  }

  await persistIdentity({ publicKey: derivedPublicKey, privateKey });
}

export class StaleRecoveryBackupError extends Error {
  constructor() {
    super("This backup doesn't match your account's current key - it may be from an identity that's since been replaced.");
    this.name = "StaleRecoveryBackupError";
  }
}

/**
 * Encrypts and uploads a backup of THIS device's current identity, protected by
 * the given secret. The secret and the key derived from it never leave the
 * browser - the server only ever stores opaque ciphertext plus the salt needed to
 * re-derive the same key next time. Always overwrites whatever backup existed
 * before (this is the explicit "set/replace my recovery secret" action) - see
 * ensureAutomaticBackupExists for the non-destructive auto-enroll version.
 */
export async function setUpRecoveryBackup(secret: string, isAutomatic: boolean): Promise<void> {
  const identity = await ensureIdentity();
  const salt = crypto.generateRecoverySalt();
  const encryptedPrivateKey = crypto.encryptPrivateKeyForBackup(identity.privateKey, secret, salt);
  await api.post("/api/users/me/recovery-backup", { salt, encryptedPrivateKey, isAutomatic });
}

/**
 * The automatic default: every account gets its identity backed up using its own
 * login password as the secret, unless the person has explicitly set a separate
 * passphrase (in which case this must NEVER overwrite that) or explicitly turned
 * backup off entirely. Only creates a backup when NONE exists yet - critical,
 * since silently overwriting an existing custom-passphrase backup with the
 * password-derived default would be a silent security downgrade the person never
 * asked for. Safe to call on every successful login; a no-op once a backup of
 * either kind already exists.
 */
export async function ensureAutomaticBackupExists(loginPassword: string): Promise<void> {
  const existing = await checkServerRecoveryBackup();
  if (existing) return;
  await setUpRecoveryBackup(loginPassword, true);
}

export async function deleteRecoveryBackup(): Promise<void> {
  await api.delete("/api/users/me/recovery-backup");
}

export async function clearIdentity() {
  identityCache = null;
  await store.clearLocalStore();
}

/**
 * Forgets the in-memory cached identity WITHOUT touching anything else in the
 * local store - unlike clearIdentity above (a full logout), this is for
 * multi-account switching, where cached messages and conversation keys should
 * stay warm for a fast switch back, only the active identity actually changes.
 *
 * This exists because of a real bug found in testing: ensureIdentity() and
 * hasLocalIdentity() both check this module-level cache BEFORE ever looking at
 * IndexedDB. Swapping "self" in storage (via snapshotActiveIdentity /
 * restoreAccountIdentity in local-store.ts) without also invalidating this
 * cache meant establishIdentity() for a newly-added account would see the
 * OUTGOING account's cached keys, decide "I already have a local identity,"
 * and silently keep using them - the new account would appear to log in
 * successfully while every one of its own messages stayed undecryptable,
 * since they were never encrypted for the wrong account's key in the first
 * place. Call this before any operation that's about to change which
 * account's identity is active in storage.
 */
export function invalidateIdentityCache() {
  identityCache = null;
}

/**
 * Fetches and locally caches every epoch's key this device currently has a
 * sealed copy of for a conversation, overwriting whatever was cached before -
 * the shared refresh step behind both getConversationKeyForEpoch (fetch-if-
 * missing) and the self-healing retry a decrypt failure triggers (force a
 * fresh copy in case the cached one is simply wrong, see
 * evictCachedConversationKey's own comment for why that can happen).
 */
async function fetchAndCacheAllConversationKeys(conversationId: string): Promise<void> {
  const identity = await ensureIdentity();
  const { keys } = await api.get<{ keys: { keyEpoch: number; sealedKey: string }[] }>(
    `/api/conversations/${conversationId}/key`
  );
  for (const { keyEpoch, sealedKey } of keys) {
    const key = crypto.unsealConversationKey(sealedKey, identity.publicKey, identity.privateKey);
    await store.cacheConversationKey(conversationId, keyEpoch, key);
  }
}

/**
 * Resolves the key for a SPECIFIC key epoch - what DECRYPTING an existing
 * message needs, since the message itself already records exactly which
 * epoch encrypted it (see migration 0049). Never resolves to "whichever
 * epoch happens to be cached" the way the pre-rotation single-key design
 * did - an old message stays readable with its own original epoch's key
 * even after the conversation has since moved on to a newer one.
 */
export async function getConversationKeyForEpoch(conversationId: string, keyEpoch: number): Promise<Uint8Array> {
  const cached = await store.getCachedConversationKey(conversationId, keyEpoch);
  if (cached) return cached;
  await fetchAndCacheAllConversationKeys(conversationId);
  const afterFetch = await store.getCachedConversationKey(conversationId, keyEpoch);
  if (!afterFetch) throw new DecryptionError(`No key available for epoch ${keyEpoch}`);
  return afterFetch;
}

/**
 * Forces a fresh fetch+unseal of every epoch's key from the server, ignoring
 * (and then overwriting) whatever's locally cached. Used as a self-healing
 * retry when a message fails to decrypt - see local-store.ts's
 * evictCachedConversationKey for why a cached key can go stale.
 */
export async function refetchConversationKey(conversationId: string): Promise<void> {
  await fetchAndCacheAllConversationKeys(conversationId);
}

/**
 * Resolves the CURRENT (latest) key for ENCRYPTING a new outgoing message.
 * serverCurrentEpoch is the conversation's own current_key_epoch, as already
 * known from the conversation object the caller already has (via the
 * conversations list, kept fresh by React Query same as everything else) -
 * comparing against it is what stops a device that missed a rotation while
 * offline from silently continuing to encrypt with a stale, already-rotated-
 * away-from epoch that a removed member could still read. Returns which
 * epoch it actually used, since the caller has to include this when sending
 * the message - the server never sees the key itself, so only the client
 * that did the actual encrypting can say which epoch it was.
 */
export async function getLatestConversationKey(
  conversationId: string,
  serverCurrentEpoch: number
): Promise<{ key: Uint8Array; keyEpoch: number }> {
  const cachedLatest = await store.getLatestCachedKeyEpoch(conversationId);
  if (cachedLatest === null || cachedLatest < serverCurrentEpoch) {
    await fetchAndCacheAllConversationKeys(conversationId);
  }
  const key = await store.getCachedConversationKey(conversationId, serverCurrentEpoch);
  if (!key) throw new DecryptionError(`No key available for current epoch ${serverCurrentEpoch}`);
  return { key, keyEpoch: serverCurrentEpoch };
}

export async function encryptForConversation(conversationId: string, serverCurrentEpoch: number, plaintext: string) {
  const { key, keyEpoch } = await getLatestConversationKey(conversationId, serverCurrentEpoch);
  return { ...crypto.encryptMessage(plaintext, key, conversationId), keyEpoch };
}

export async function decryptForConversation(
  conversationId: string,
  content: string,
  nonce: string,
  keyEpoch: number
): Promise<string> {
  const key = await getConversationKeyForEpoch(conversationId, keyEpoch);
  return crypto.decryptMessage(content, nonce, key, conversationId);
}

export async function encryptMediaForConversation(
  conversationId: string,
  serverCurrentEpoch: number,
  bytes: Uint8Array
): Promise<{ bytes: Uint8Array; keyEpoch: number }> {
  const { key, keyEpoch } = await getLatestConversationKey(conversationId, serverCurrentEpoch);
  return { bytes: crypto.encryptBytes(bytes, key, conversationId), keyEpoch };
}

export async function decryptMediaForConversation(
  conversationId: string,
  bytes: Uint8Array,
  keyEpoch: number
): Promise<Uint8Array> {
  const key = await getConversationKeyForEpoch(conversationId, keyEpoch);
  return crypto.decryptBytes(bytes, key, conversationId);
}

/** Generates a fresh conversation key and seals it for both sides of a new direct chat. */
export async function prepareDirectConversationKeys(otherUserPublicKey: string) {
  const identity = await ensureIdentity();
  const key = crypto.generateConversationKey();
  return {
    key,
    sealedKeyForMe: crypto.sealConversationKey(key, identity.publicKey),
    sealedKeyForThem: crypto.sealConversationKey(key, otherUserPublicKey)
  };
}

/** Same idea as prepareDirectConversationKeys above, just for Saved Messages -
 * a fresh conversation key sealed only for this same identity, since there's
 * no "other person" to seal a second copy for. */
export async function prepareSavedMessagesKey() {
  const identity = await ensureIdentity();
  const key = crypto.generateConversationKey();
  return { key, sealedKeyForMe: crypto.sealConversationKey(key, identity.publicKey) };
}

/** Generates a fresh conversation key and seals it for the creator + every initial member. */
export async function prepareGroupConversationKeys(members: { userId: string; publicKey: string }[]) {
  const identity = await ensureIdentity();
  const key = crypto.generateConversationKey();
  const sealedMembers = members.map((m) => ({
    userId: m.userId,
    sealedKey: crypto.sealConversationKey(key, m.publicKey)
  }));
  return { key, members: sealedMembers };
}

/**
 * The actual fix for "a removed member can still decrypt new group
 * messages" (see migration 0049's design comment for the full picture).
 * Called by the admin's own client right after a removal succeeds, while
 * they're still a participant (rotation has to be initiated by someone who
 * still is one - see rotateGroupKey's own backend comment): generates a
 * genuinely fresh symmetric key, seals it only for the members who remain,
 * caches it locally so the admin's own device has the new key immediately
 * without needing a round-trip to re-fetch it, then submits the sealed
 * copies to the server.
 *
 * remainingMembers must already exclude the member who was just removed -
 * this function doesn't know who was removed, only who to seal for, so
 * getting that list right is the caller's responsibility (see
 * useRotateGroupKey's own comment for where that list comes from).
 */
export async function rotateGroupKeyExcludingRemoved(
  conversationId: string,
  remainingMembers: { userId: string; publicKey: string }[]
): Promise<void> {
  const key = crypto.generateConversationKey();
  const newSealedKeys = remainingMembers.map((m) => ({
    userId: m.userId,
    sealedKey: crypto.sealConversationKey(key, m.publicKey)
  }));

  const { newEpoch } = await api.post<{ newEpoch: number }>(`/api/conversations/${conversationId}/rotate-key`, {
    newSealedKeys
  });

  // Cache locally right away for the admin's own device - the sealed copy
  // just submitted for their own userId above is equivalent to what a
  // getConversationKeyForEpoch(conversationId, newEpoch) fetch-and-unseal
  // round-trip would produce, so doing it directly here avoids that
  // unnecessary extra request for the one device that already has the
  // plaintext key in hand at this exact moment.
  await store.cacheConversationKey(conversationId, newEpoch, key);
}

/** Seals an existing conversation's CURRENT (latest) key for a member being
 * added later, matching how a new member has always gained access to future
 * messages. Returns which epoch it sealed, since the backend's add-member
 * insert needs to record that alongside the sealed key itself (see migration
 * 0049 - a sealed key row is no longer meaningful without knowing which
 * epoch it's for). Caller must already be a participant. */
export async function sealExistingKeyForNewMember(
  conversationId: string,
  serverCurrentEpoch: number,
  newMemberPublicKey: string
): Promise<{ sealedKey: string; keyEpoch: number }> {
  const { key, keyEpoch } = await getLatestConversationKey(conversationId, serverCurrentEpoch);
  return { sealedKey: crypto.sealConversationKey(key, newMemberPublicKey), keyEpoch };
}

/** Cache a newly-created conversation's key locally right after the server
 * confirms creation - always epoch 1, since a brand-new conversation has
 * never been rotated. */
export async function cacheNewConversationKey(conversationId: string, key: Uint8Array) {
  await store.cacheConversationKey(conversationId, 1, key);
}

export { DecryptionError } from "./crypto";
export * as localStore from "./local-store";

export type ConversationKeyStatus = "ok" | "pending" | "broken" | "unknown";

/**
 * Directly checks whether this device has a working key for a conversation,
 * independent of whether there's any message history to infer it from. Message-
 * based inference (does every message fail to decrypt / show as pending) only
 * works when there ARE messages - a brand-new or empty conversation this device
 * has no key for would otherwise look identical to a perfectly fine one, letting
 * someone type and send into a conversation that will always silently fail.
 */
export async function checkConversationKeyStatus(
  conversationId: string,
  serverCurrentEpoch: number
): Promise<ConversationKeyStatus> {
  try {
    await getLatestConversationKey(conversationId, serverCurrentEpoch);
    return "ok";
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return "pending";
    if (!(err instanceof crypto.DecryptionError)) return "unknown";
    try {
      await refetchConversationKey(conversationId);
      return "ok";
    } catch (retryErr) {
      if (retryErr instanceof ApiError && retryErr.status === 404) return "pending";
      if (retryErr instanceof crypto.DecryptionError) return "broken";
      return "unknown";
    }
  }
}
